import { useCallback, useEffect, useRef, useState } from "react";
import { TerminalSquare, Upload, Download, FolderOpen, FileCode2, Trash2, ChevronRight, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API, getToken } from "@/lib/api";
import ResourceTable from "@/components/resources/ResourceTable";
import { useSchema } from "@/context/SchemaContext";

const QUICK = [
  "help",
  "tools",
  "keys",
  "stats",
  "bootstrap",
  "ls -la",
  "python3 --version && node --version",
  "env",
  "res list domains",
  "http GET https://api.github.com/zen",
  "ai أنشئ سكربت بايثون يطبع حالة كل المصانع من $AQL4_API/stats"
];
const LIME = "#A3E635";

export default function ShellPage() {
  const { resources } = useSchema();
  const [lines, setLines] = useState([{ kind: "sys", text: "الشِل السيادي — aql4 · طبقة الأدوات الخارجية متاحة: Python, Node, npm, pip, git, curl, bash, ls, grep, ffmpeg. اكتب tools أو bootstrap لمراجعة البيئة أو إعادة بناء runtime." }]);
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const [hi, setHi] = useState(-1);
  const [files, setFiles] = useState({ path: "", items: [] });
  const [side, setSide] = useState("files");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const loadFiles = useCallback((p = files.path) => api.get("/shell/files", { params: { path: p } }).then((r) => setFiles(r.data)).catch(() => {}), [files.path]);
  useEffect(() => { loadFiles(""); api.get("/shell/history", { params: { limit: 30 } }).then((r) => setHist(r.data.map((h) => h.command).reverse())).catch(() => {}); }, [loadFiles]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [lines]);

  const run = async (c) => {
    const command = (c ?? cmd).trim();
    if (!command || busy) return;
    setCmd(""); setHi(-1);
    if (command === "clear") { setLines([]); return; }
    setLines((l) => [...l, { kind: "cmd", text: command }]);
    setHist((h) => [...h.filter((x) => x !== command), command]);
    setBusy(true);
    try {
      const { data } = await api.post("/shell/exec", { command, cwd: files.path });
      setLines((l) => [...l,
        ...(data.stdout ? [{ kind: "out", text: data.stdout }] : []),
        ...(data.stderr ? [{ kind: "err", text: data.stderr }] : []),
        { kind: "meta", text: `↳ exit ${data.code} · ${data.ms}ms` }]);
      loadFiles();
    } catch (e) { setLines((l) => [...l, { kind: "err", text: errMsg(e) }]); }
    setBusy(false);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); run(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); const i = hi < 0 ? hist.length - 1 : Math.max(0, hi - 1); setHi(i); setCmd(hist[i] || ""); }
    else if (e.key === "ArrowDown") { e.preventDefault(); const i = hi + 1; if (i >= hist.length) { setHi(-1); setCmd(""); } else { setHi(i); setCmd(hist[i]); } }
  };

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/shell/upload", fd, { params: { path: files.path } }); toast.success(`تم رفع ${f.name}`); loadFiles(); } catch (e) { toast.error(errMsg(e)); }
  };
  const download = (p) => window.open(`${API}/shell/download?path=${encodeURIComponent(p)}&token=${getToken()}`, "_blank");

  return (
    <div className="h-full flex flex-col" data-testid="shell-page">
      <div className="px-4 sm:px-8 pt-6 pb-3 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl grid place-items-center border" style={{ color: LIME, background: `${LIME}14`, borderColor: `${LIME}44`, boxShadow: `0 0 40px -12px ${LIME}` }}><TerminalSquare size={22} /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-50">الشِل السيادي</h1>
            <p className="text-xs text-slate-400">برمجة · أوامر · جسر التكامل الخارجي — يُخرج ويُدخل ويبرمج أنظمة المصنع</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="shell-quick-commands">
          {QUICK.map((q) => <button key={q} data-testid={`shell-quick-${q.split(" ")[0]}`} onClick={() => run(q)} className="font-mono text-[11px] rounded-lg border border-white/[0.08] bg-white/[0.03] hover:bg-lime-400/10 hover:border-lime-400/30 px-2.5 py-1 text-slate-300 transition-colors" dir="ltr">{q.length > 34 ? q.slice(0, 34) + "…" : q}</button>)}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col bg-[#05080F]">
          <div className="flex-1 overflow-y-auto p-4 font-mono text-[13px] leading-6" dir="ltr" data-testid="shell-output">
            {lines.map((l, i) => (
              <pre key={i} className={`whitespace-pre-wrap break-words ${l.kind === "cmd" ? "text-lime-300 mt-3" : l.kind === "err" ? "text-red-300" : l.kind === "meta" ? "text-slate-600 text-[11px]" : l.kind === "sys" ? "text-cyan-300/80" : "text-slate-200"}`}>
                {l.kind === "cmd" ? `aql4@factory:~/${files.path} $ ${l.text}` : l.text}
              </pre>
            ))}
            {busy && <p className="text-slate-500 flex items-center gap-2 mt-2"><Loader2 size={13} className="animate-spin" /> executing…</p>}
            <div ref={endRef} />
          </div>
          <div className="border-t border-white/[0.06] p-3 flex items-center gap-2 bg-[#070B14]" dir="ltr">
            <span className="font-mono text-lime-300 text-sm shrink-0">$</span>
            <input ref={inputRef} data-testid="shell-input" value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={onKey} autoFocus spellCheck={false}
              placeholder="command… (help / ai <طلب> / http GET <url> / python3 …)" className="flex-1 bg-transparent font-mono text-sm text-slate-100 placeholder:text-slate-600 outline-none" />
            <button data-testid="shell-run-button" onClick={() => run()} disabled={busy || !cmd.trim()} className="h-9 px-3 rounded-lg text-slate-950 font-semibold text-xs flex items-center gap-1 disabled:opacity-40" style={{ background: LIME }}><Play size={13} /> تنفيذ</button>
          </div>
        </div>

        <aside className="hidden lg:flex w-80 shrink-0 flex-col border-r border-white/[0.06] bg-[#090D16]/60" data-testid="shell-sidebar">
          <div className="flex border-b border-white/[0.06]">
            {[["files", "الملفات"], ["scripts", "السكربتات"]].map(([k, l]) => (
              <button key={k} data-testid={`shell-side-${k}`} onClick={() => setSide(k)} className={`flex-1 h-11 text-xs transition-colors ${side === k ? "text-lime-300 border-b-2 border-lime-300" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          {side === "files" && (
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[11px] text-slate-500" dir="ltr">/workspace/{files.path}</p>
                <div className="flex gap-1">
                  {files.path && <button data-testid="shell-files-up" onClick={() => loadFiles(files.path.split("/").slice(0, -1).join("/"))} className="h-7 px-2 rounded-md bg-white/5 text-slate-300 text-[11px]">↑</button>}
                  <button data-testid="shell-upload-button" onClick={() => fileRef.current?.click()} className="h-7 px-2 rounded-md bg-lime-400/10 text-lime-300 text-[11px] flex items-center gap-1"><Upload size={12} /> رفع</button>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="shell-upload-input" />
                </div>
              </div>
              {files.items.length === 0 && <p className="text-xs text-slate-600 text-center py-6">المجلد فارغ — ارفع ملفات أو أنشئها بالأوامر</p>}
              {files.items.map((f) => (
                <div key={f.path} data-testid={`shell-file-${f.name}`} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/[0.04]">
                  {f.dir ? <FolderOpen size={14} className="text-amber-300" /> : <FileCode2 size={14} className="text-slate-400" />}
                  <button onClick={() => f.dir ? loadFiles(f.path) : run(`cat ${JSON.stringify(f.path)}`)} className="flex-1 text-right font-mono text-xs text-slate-200 truncate" dir="ltr">{f.name}</button>
                  {!f.dir && <>
                    <span className="text-[10px] text-slate-600 font-mono">{f.size > 1024 ? `${(f.size / 1024).toFixed(1)}K` : `${f.size}B`}</span>
                    <button onClick={() => download(f.path)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-lime-300"><Download size={13} /></button>
                    <button onClick={() => run(`rm ${JSON.stringify(f.path)}`)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-300"><Trash2 size={13} /></button>
                  </>}
                  {f.dir && <ChevronRight size={12} className="text-slate-600 rotate-180" />}
                </div>
              ))}
            </div>
          )}
          {side === "scripts" && resources.scripts && (
            <div className="flex-1 overflow-y-auto p-3 text-xs">
              <p className="text-slate-500 mb-2">احفظ سكربتاتك وشغّلها بـ <code className="text-lime-300">run &lt;name&gt;</code></p>
              <ResourceTable resource="scripts" spec={resources.scripts} color={LIME} />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
